import { useDroppable } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { KanbanCard } from './KanbanCard';
import { useLanguage } from '../hooks/useLanguage';

const colorClasses = {
  blue: 'bg-blue-100 text-blue-800 border-blue-300',
  yellow: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  purple: 'bg-purple-100 text-purple-800 border-purple-300',
  orange: 'bg-orange-100 text-orange-800 border-orange-300',
  green: 'bg-green-100 text-green-800 border-green-300',
  red: 'bg-red-100 text-red-800 border-red-300',
};

export const KanbanColumn = ({
  status,
  leads,
  onOpenDetail,
  onDelete,
  selectionMode = false,
  selectedLeads = new Set(),
  onToggleSelection,
  onSelectAllInStatus
}) => {
  const { t } = useLanguage();
  const { setNodeRef } = useDroppable({
    id: status.id,
  });

  const leadIds = leads.map((lead) => lead.id);

  // Check if all leads in this column are selected
  const allSelectedInColumn = leads.length > 0 && leads.every(lead => selectedLeads.has(lead.id));

  const handleSelectAllInColumn = () => {
    if (onSelectAllInStatus) {
      onSelectAllInStatus(status.id, leads);
    }
  };

  return (
    <div className="flex-shrink-0 w-80 flex flex-col h-full">
      <div
        className={`
          ${colorClasses[status.color] || colorClasses.blue}
          px-4 py-2 rounded-t-lg border-t-4 flex items-center justify-between flex-shrink-0
        `}
      >
        <h3 className="font-semibold">{status.name}</h3>
        <div className="flex items-center gap-2">
          {selectionMode && leads.length > 0 && (
            <button
              onClick={handleSelectAllInColumn}
              className="text-xs bg-white/70 hover:bg-white px-2 py-1 rounded transition-colors"
              title={allSelectedInColumn ? t('deselectAllInStatus') : t('selectAllInStatus')}
            >
              {allSelectedInColumn ? (
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              )}
            </button>
          )}
          <span className="text-sm font-medium bg-white/50 px-2 py-1 rounded">
            {leads.length}
          </span>
        </div>
      </div>

      <div
        ref={setNodeRef}
        className="bg-gray-50 rounded-b-lg p-3 space-y-3 flex-1 overflow-y-auto"
      >
        <SortableContext
          items={leadIds}
          strategy={verticalListSortingStrategy}
        >
          {leads.map((lead) => (
            <KanbanCard
              key={lead.id}
              lead={lead}
              onOpenDetail={onOpenDetail}
              onDelete={onDelete}
              selectionMode={selectionMode}
              isSelected={selectedLeads.has(lead.id)}
              onToggleSelection={onToggleSelection}
            />
          ))}
        </SortableContext>

        {leads.length === 0 && (
          <div className="text-center py-8 text-gray-400">
            <svg
              className="w-12 h-12 mx-auto mb-2"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
            <p className="text-sm">{t('noLeads')}</p>
          </div>
        )}
      </div>
    </div>
  );
};
